import type { LucideIcon } from 'lucide-react';
import type {
  ArrayValues,
  CamelCasedProperties,
  CamelCasedPropertiesDeep,
  JsonObject,
  RequiredDeep,
  SetOptional,
} from 'type-fest';

import type {
  AnchoreImage,
  ImageContentSummary,
  ImageDetail,
  ImageAncestor as RawImageAncestor,
  ImageArtifact,
  Source,
  SourcePolicyEvaluation,
  ContentJavaPackageResponse,
  ContentMalwareResponse,
  ContentPackageResponse,
  FileContentSearchList,
  RetrievedFileList,
  SecretSearchList,
  PolicyRecord,
  ImagePackageVulnerability as RawImagePackageVulnerability,
  PackageVulnerability as RawSourcePackageVulnerability,
  ImagePackageVulnerabilityResponse,
  SourcePackageVulnerabilityResponse,
  STIGMetadataResponse,
  SourceContentPackageResponse,
} from '@/api/api';
import type { EntityServiceResponse, Permissions } from '@models';
import type { ServiceResponse } from '@models/common.models';
import type {
  Policy,
  PolicyEvaluationFinding,
  RawPolicyEvaluation,
  MappingRule,
} from '@features/policy/policies.models';

import type { ArtifactType } from './common/artifact-type';

export { ArtifactType } from './common/artifact-type';

export * from './artifact-analysis/vulnerability-list/vulnerability-annotations.models';

export type ArtifactList = Source[] | ImageArtifact[];

/**
 * String literal union derived from ArtifactType enum values.
 * @deprecated Prefer using `ArtifactType` enum directly.
 */
export type ArtifactId = `${ArtifactType}`;

/**
 * Legacy configuration object for artifact types.
 * @see artifactTypes in artifacts.const.ts
 */
export interface ArtifactTypeConfig {
  id: ArtifactType;
  typeName: string;
  title: string;
  titleSingular: string;
  icon: LucideIcon;
}

export type { ImageArtifact, SourcePolicyEvaluation, Source, ImageDetail };

export type ArtifactImageContent = { metadata?: { distro?: string } };

export type ImageAncestor = RequiredDeep<RawImageAncestor>;

export type AnchoreImageWithAncestors = AnchoreImage & {
  ancestors?: ImageAncestor[];
  ancestorsError?: any;
  image_content?: ArtifactImageContent;
};

export type ImageAnnotation = { id: string; key: string; val: string };

export type PackageVulnerability =
  RawImagePackageVulnerability | RawSourcePackageVulnerability;

export type ImageVulnerabilityData =
  CamelCasedProperties<ImagePackageVulnerabilityResponse>;

export type SourceVulnerabilityData =
  CamelCasedProperties<SourcePackageVulnerabilityResponse>;

export type ArtifactVulnerabilityData =
  ImageVulnerabilityData | SourceVulnerabilityData;

/**
 * Known annotation keys used throughout the application
 */
export enum KnownAnnotations {
  /**
   * Controls Extended Update Support (EUS) behavior for vulnerability scanning
   */
  EXTENDED_SUPPORT = 'anchore.user/extended_support',
  /**
   * Marks a specific ancestor image as the user-preferred base image
   */
  MARKED_BASE_IMAGE = 'anchore.user/marked_base_image',
}

export type Severity =
  'Critical' | 'High' | 'Medium' | 'Low' | 'Negligible' | 'Unknown';
export type {
  AnchoreImage,
  AnchoreImageTagSummary,
  ContentFilesResponse,
  ContentJavaPackageResponse,
  ContentMalwareResponse,
  ContentPackageResponse,
  FeedMetadata,
  FileContentSearchList,
  ImageContent,
  ImagePackageVulnerabilityResponse,
  JavaPackageContent,
  MalwareScan,
  PackageContent,
  PolicyEvaluationProblem,
  RetrievedFile,
  SecretSearchList,
  ServiceList,
  SourcePackageVulnerabilityResponse,
  STIGMetadataResponse,
  STIGMetadataResponseList,
} from '@/api/api';

export interface SummaryImageContext {
  context: string;
  inventoryType: string;
  lastSeen: string;
}

export interface SummaryImage {
  analysisStatus: string;
  analyzedAt: number;
  createdAt: number;
  imageDigest: string;
  imageId: string;
  imageStatus: string;
  parentDigest: string;
  tagDetectedAt: number;
  contexts?: SummaryImageContext[] | false;
}

export interface SummaryTag {
  tag: string;
  active: boolean;
  subscription_id: string | false;
  alert: {
    active: boolean;
    subscription_id: string | false;
  };
  history: SummaryImage[];
}

export interface SummaryRepository {
  repository: string;
  active: boolean;
  subscription_id: string | false;
  alert: {
    active: boolean;
    subscription_id: string | false;
  };
  tags: SummaryTag[];
}

export interface SummaryRegistry {
  registry: string;
  repositories: SummaryRepository[];
}

export type ArtifactCompliance = [
  RawPolicyEvaluation | SourcePolicyEvaluation,
  PolicyRecord[],
];

export interface ComplianceAlertItem {
  last_updated: string;
  resource?: {
    image_digest?: string;
  };
}

export interface FetchComplianceAlertResponse {
  type?: string;
  errorMsg?: {
    message?: string;
  };
}

export interface UIContentFilesContent {
  filename?: string;
  gid?: number;
  linkdest?: string | null;
  mode?: string;
  modeStr: string;
  sha256?: string | null;
  size?: number;
  type?: string;
  uid?: number;
}

export interface UIContentFilesResponse {
  image_digest?: string;
  content_type?: string;
  content?: UIContentFilesContent[];
}

export type UIImageContentResponse =
  | ContentPackageResponse
  | ContentJavaPackageResponse
  | UIContentFilesResponse
  | ContentMalwareResponse;

export type UISourceContentResponse = SourceContentPackageResponse;
export type UIContentResponse =
  UIImageContentResponse | UISourceContentResponse;

export type ArtifactContentItem = Required<UIContentResponse>['content'][0];

export type ArtifactContents = {
  fetching: boolean;
  errorMsg: any;
  error: boolean;
  contents: UIContentResponse | false;
};

export interface GetArtifactContentsResponse extends ServiceResponse {
  data: UIContentResponse;
  ancestorsError?: any;
}

export type ContentSummaryResponse = CamelCasedPropertiesDeep<
  RequiredDeep<ImageContentSummary>
>;

export type ContentSummaryItem = ArrayValues<ContentSummaryResponse['items']>;

export interface GetArtifactContentSummaryResponse extends ServiceResponse {
  data: ContentSummaryItem[];
}

export type ArtifactEntityType = 'images' | 'sources';

export type ArtifactContentTypeConfig = Record<
  string,
  {
    icon: string;
    label?: string;
  }
>;

export interface ArtifactContentMenuItem {
  contentType: string;
  disabled: boolean;
  icon: string;
  isGroupStart: boolean;
  isGroupEnd: boolean;
  label: string;
}

type ArtifactArtifactsResponse =
  FileContentSearchList | RetrievedFileList | SecretSearchList;

export interface GetArtifactArtifactsResponse extends ServiceResponse {
  data: {
    content: ArtifactArtifactsResponse;
    content_type: string;
    imageDigest: string;
  };
}

export type ArtifactChangelog = {
  ctype: string;
  prevImage: AnchoreImage;
  currImage: AnchoreImage;
  changed: {
    name: string;
    data: {
      curr: ArtifactContentItem;
      prev: ArtifactContentItem;
    };
    status: 'changed';
  }[];
  added: {
    name: string;
    data: ArtifactContentItem;
    status: 'added';
  }[];
  removed: {
    name: string;
    data: ArtifactContentItem;
    status: 'removed';
  }[];
};
export type ArtifactChangelogItem = {
  name: string;
  status: 'changed' | 'added' | 'removed';
  data:
    | {
        curr: ArtifactContentItem;
        prev: ArtifactContentItem;
      }
    | ArtifactContentItem;
};

export interface ArtifactAnalysisImageRouteInfo {
  id: string;
  registry: string;
  repository: string;
  tag: string;
  account?: string;
  base?: string;
  path?: string;
  context?: string;
}

export interface ArtifactAnalysisSourceRouteInfo {
  id: string;
  context?: string;
}

export type ArtifactAnalysisListRouteInfo =
  ArtifactAnalysisImageRouteInfo | ArtifactAnalysisSourceRouteInfo;

export interface GetArtifactChangelogResponse extends ServiceResponse {
  data: ArtifactChangelog;
}

export interface ArtifactAnalysisMalwareScanningStatus {
  isEnabled: boolean;
  isEditable?: boolean;
}

// --- Action types ------------------------------------------------------------
export interface GetArtifactComplianceResponse extends ServiceResponse {
  data: ArtifactCompliance;
  ancestorsError?: any;
}
export interface GetArtifactComplianceQueryParams {
  tag: string;
  policyId?: string;
  interactive?: boolean;
  listPolicies?: boolean;
}
export interface GetArtifactComplianceParams {
  id: string;
  artifactType: ArtifactEntityType;
  params: GetArtifactComplianceQueryParams;
}

export interface FormatArtifactExportParams {
  artifactType: ArtifactEntityType | 'sboms';
  id: string;
  data: JsonObject;
  format: 'json' | 'csv';
}

export type FormatArtifactExportResponse = EntityServiceResponse<string, true>;

// --- STIG specific types -----------------------------------------------------
export type StigItem = STIGMetadataResponse;

export interface StigModalData {
  evaluationUuid: string;
  imageDigest: string;
  stigProfile?: string;
  onSuccess?: () => void;
}

export interface StigActionsDropdownProps {
  stigItem: StigItem;
  imageDigest: string;
  onDeleteSuccess: () => void;
  canDelete: boolean;
}

export interface StigReportProps {
  routeInfo: {
    id: string;
  };
  permissions?: Permissions;
}

export type PackageDisplayDetails = {
  icon?: string;
  contentType: string;
  label?: string;
};

export type PackageType =
  | 'java'
  | 'npm'
  | 'gem'
  | 'python'
  | 'apkg'
  | 'rpm'
  | 'dpkg'
  | 'kb'
  | 'nuget'
  | 'go'
  | 'nix'
  | 'alpm';

// --- Allowlist Item Edit Modal Types -----------------------------------------

export interface AllowlistItemEditModalData {
  bundle: Policy;
  finding: PolicyEvaluationFinding;
  gate: string;
  mapping?: SetOptional<
    CamelCasedPropertiesDeep<MappingRule>,
    'image' | 'registry' | 'repository'
  >;
}

export type ArtifactModalInstances = {
  allowlistItemEdit: AllowlistItemEditModalData;
};
